import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import ReferralProgram from '../../components/referral/ReferralProgram';
import referralService from '../../services/referralService';

const Referrals = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showComingSoonModal, setShowComingSoonModal] = useState(true);
  const [referralStats, setReferralStats] = useState({
    totalReferrals: 0,
    successfulReferrals: 0,
    totalEarnings: 0
  });

  useEffect(() => {
    const fetchReferralStats = async () => {
      if (!currentUser) return;

      try {
        setLoading(true);
        
        // Get user's referrals from Firestore
        const userReferrals = await referralService.getUserReferrals(currentUser.uid);
        
        // Calculate total earnings from referrals
        const totalEarnings = await referralService.calculateTotalEarnings(currentUser.uid);
        
        setReferralStats({
          totalReferrals: userReferrals.length,
          successfulReferrals: userReferrals.filter(r => r.status === 'completed').length,
          totalEarnings: totalEarnings
        });
      } catch (err) {
        console.error('Error fetching referral stats:', err);
        setError('Failed to load referral statistics. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchReferralStats();
  }, [currentUser]);

  return (
    <div className="relative">
      {/* Coming Soon Modal */}
      {showComingSoonModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop with blur effect */}
          <div 
            className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
            onClick={() => setShowComingSoonModal(false)}
          ></div>
          
          {/* Modal Content */}
          <div className="relative bg-white rounded-lg shadow-xl p-8 max-w-md mx-4 text-center">
            <div className="mb-6">
              <div className="h-16 w-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Coming Soon!</h3>
              <p className="text-gray-600">
                Our referral campaign is currently being prepared and will be launched soon. 
                Stay tuned for exciting rewards and benefits!
              </p>
            </div>
            
            <button
              onClick={() => setShowComingSoonModal(false)}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition duration-200"
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {/* Main Content with blur effect when modal is open */}
      <div className={`${showComingSoonModal ? 'blur-sm pointer-events-none' : ''} transition-all duration-300`}>
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">Referral Program</h2>
          <p className="text-gray-600">Invite friends, earn rewards, and grow your network</p>
        </div>
      
      {error && (
        <div className="mb-6 bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}
      
      <ReferralProgram />
      
      {/* Additional Referral Benefits */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Earn Cash Rewards</h3>
          <p className="text-sm text-gray-600">
            Receive ₱500 for each friend who completes their first campaign. There's no limit to how much you can earn!
          </p>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Build Your Network</h3>
          <p className="text-sm text-gray-600">
            Connect with other influencers and expand your professional network. Collaborate on campaigns and share opportunities.
          </p>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="h-12 w-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Unlock Exclusive Benefits</h3>
          <p className="text-sm text-gray-600">
            Reach referral milestones to unlock exclusive perks, priority access to premium campaigns, and special rewards.
          </p>
        </div>
      </div>
      
      {/* Referral Tiers */}
      <div className="mt-8 bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-800">Referral Tiers & Benefits</h3>
        </div>
        <div className="p-6">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tier</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Requirement</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Benefits</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-8 w-8 bg-gray-100 rounded-full flex items-center justify-center mr-3">
                        <span className="text-gray-600 font-bold">1</span>
                      </div>
                      <div className="text-sm font-medium text-gray-900">Bronze Referrer</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">1-5 successful referrals</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-500">
                      <ul className="list-disc list-inside">
                        <li>₱500 per successful referral</li>
                        <li>Referral badge on your profile</li>
                      </ul>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-8 w-8 bg-silver-100 rounded-full flex items-center justify-center mr-3 bg-gray-200">
                        <span className="text-gray-700 font-bold">2</span>
                      </div>
                      <div className="text-sm font-medium text-gray-900">Silver Referrer</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">6-15 successful referrals</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-500">
                      <ul className="list-disc list-inside">
                        <li>₱500 per successful referral</li>
                        <li>+5% bonus on all your campaign earnings</li>
                        <li>Priority access to new campaigns</li>
                      </ul>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-8 w-8 bg-yellow-100 rounded-full flex items-center justify-center mr-3">
                        <span className="text-yellow-600 font-bold">3</span>
                      </div>
                      <div className="text-sm font-medium text-gray-900">Gold Referrer</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">16+ successful referrals</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-500">
                      <ul className="list-disc list-inside">
                        <li>₱700 per successful referral (increased bonus!)</li>
                        <li>+10% bonus on all your campaign earnings</li>
                        <li>Priority access to premium campaigns</li>
                        <li>Exclusive invitations to influencer events</li>
                      </ul>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      
      {/* FAQ Section */}
      <div className="mt-8 bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-800">Frequently Asked Questions</h3>
        </div>
        <div className="p-6">
          <div className="space-y-6">
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-2">How do I earn referral bonuses?</h4>
              <p className="text-sm text-gray-600">
                Share your unique referral link with friends. When they sign up and complete their first campaign review, you'll automatically receive a ₱500 bonus in your account.
              </p>
            </div>
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-2">When will I receive my referral bonus?</h4>
              <p className="text-sm text-gray-600">
                Referral bonuses are processed within 7 days after your referred friend completes their first campaign. The bonus will be added to your available balance.
              </p>
            </div>
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-2">Is there a limit to how many people I can refer?</h4>
              <p className="text-sm text-gray-600">
                No, there's no limit! You can refer as many friends as you want and earn a bonus for each successful referral.
              </p>
            </div>
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-2">What happens if someone signs up without using my referral link?</h4>
              <p className="text-sm text-gray-600">
                To receive credit for a referral, your friend must sign up using your unique referral link. If they sign up without using your link, unfortunately, you won't receive the referral bonus.
              </p>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default Referrals;
